// "Show your license": this client asks a Mac it just connected to, checks the answer offline and keeps the
// verdict on the Mac's connection object (docs/features/license-private-step-in-mac.md › Flow 5;
// docs/features/licenses.md › Stage 2). Nothing is shown and nothing changes by the verdict in this stage — it is
// recorded and logged, one line.
//
//   → wire `license` {what: "show", nonce}                    a fresh 32-byte nonce, base64url
//   ← wire `license` {what: "license", nonce, license, proof}  the license text and the holder proof (or neither)
//
// The answer is checked by core/license-holder.js `verifyPresented`: the license's signature, the revocation list
// as last seen, and the proof — by the Mac key inside the license, over this license's id, the Mac's identity key
// THIS client pinned at pairing, and this nonce. The whole exchange stays on the direct channel to the Mac;
// nothing of it goes to the site.
//
// One seam: `showLicense(mac, link, {pinned, …})` — no DOM, no storage, no network of its own (the tests run it
// under Node: services/app/test/license-show.test.mjs). `mac.license` is null until there is a verdict, then
// {valid: true, grants, mode, refundable, keyId, id} or {valid: false, why, message}.
import { b64u } from '../core/license.js'
import { verifyPresented } from '../core/license-holder.js'

/// The one line for the log: the verdict and the kind — never the license's id, never the Mac's name.
export function licenseLine(result) {
  return result.valid
    ? 'license: valid — ' + result.grants + (result.refundable ? ', refundable' : ', private') + (result.mode === 'live' ? '' : ', ' + result.mode + ' mode')
    : 'license: not valid — ' + result.why
}

///   pinned    the Mac's identity key as this client pinned it (base64url SPKI), or null: then nothing is asked
///   keys      the signing keys this client trusts (the built-in list when left out)
///   modes     which of them count here: ['live'], or ['live', 'test'] off the real site
///   revoked   () → the revocation list as last seen (a `verifyList` result), or null
///   log       (line) → the client's log
///   wait      how long a Mac may take to answer, ms: an older Cody on the Mac never does
export function showLicense(mac, link, { pinned, keys, modes = ['live'], revoked = async () => null, log = () => {}, wait = 10000 } = {}) {
  let settled = false, timer = null
  const settle = (result) => {
    if (settled) return
    settled = true
    clearTimeout(timer)
    mac.license = result
    log(licenseLine(result))
  }
  mac.license = null
  if (!pinned) { settle({ valid: false, why: 'no-identity', message: 'This device has no pinned key for the Mac to check a license against.' }); return }
  const nonce = b64u(crypto.getRandomValues(new Uint8Array(32)))
  link.onLicense = async (message) => {
    // only the answer to this question: another nonce is an old answer, or somebody else's
    if (settled || message?.what !== 'license' || message.nonce !== nonce) return
    clearTimeout(timer)
    let list = null
    try { list = await revoked() } catch { /* no list seen: the license is checked without one */ }
    settle(await verifyPresented({ license: message.license, proof: message.proof, nonce, macIdentityKey: pinned, keyList: keys, revoked: list, modes }))
  }
  timer = setTimeout(() => settle({ valid: false, why: 'no-answer', message: 'The Mac did not answer (a Cody on the Mac from before licenses never does).' }), wait)
  link.send('license', { what: 'show', nonce })
}
